<script lang="ts">
  import { onMount, untrack } from 'svelte'
  import 'frontend/shared/stylesheets/player.sass'
  import VideoBanners from './VideoBanners.svelte'
  import SubstituteLogo from './SubstituteLogo.svelte'
  import PreAdScreen from './PreAdScreen.svelte'
  import { players, type RegisteredPlayer } from './players.svelte'
  import { lazy } from 'frontend/shared/javascripts/util/lazy.svelte'
  import type { AutoplayClearance, MainPlayer, PlaybackState } from './main_player'
  import type { AdVisibility, VideoPlayerData } from './types'
  import type PlayerClass from 'player/javascripts/player'
  import { trackEvent } from 'frontend/shared/javascripts/util/analytics'
  import { trackingContextAttribute } from 'frontend/shared/javascripts/tracking'

  const {
    video,
    show_ads: showAds,
    site,
    banners = [],
    substitute_logo: substituteLogo = null,
    startPosition,
    endPosition,
    autoplay = false,
    autoplayClearance,
    skipVisitBookkeeping = false,
    adVisibility = 'shown',
    dockOwner = false,
    onQuizRequested,
    onLearningExercisesRequested,
    onDockedChange,
    onPlaying,
    onPaused,
    onFinished,
  }: VideoPlayerData & {
    startPosition?: number
    endPosition?: number
    autoplay?: boolean
    /** What an autoplaying player waits for before it starts, e.g. a modal covering the page. */
    autoplayClearance?: AutoplayClearance
    skipVisitBookkeeping?: boolean
    adVisibility?: AdVisibility
    dockOwner?: boolean
    onQuizRequested?: () => void
    onLearningExercisesRequested?: (event: MouseEvent, url: string) => void
    onDockedChange?: (docked: boolean) => void
    onPlaying?: () => void
    onPaused?: () => void
    onFinished?: () => void
  } = $props()

  type PlayerInstance = InstanceType<typeof PlayerClass>

  let playerElement: HTMLElement
  let containerElement: HTMLElement | undefined = $state()
  let bannersElement: HTMLElement | undefined = $state()
  let substituteLogoElement: HTMLElement | undefined = $state()
  let preAdScreenElement: HTMLElement | undefined = $state()
  let preAdScreenShown = $state(false)
  let preAdScreenHeight = $state(0)

  let attached: MainPlayer | undefined
  let resolveAttached!: (main: MainPlayer) => void
  const attachedPromise = new Promise<MainPlayer>((resolve) => {
    resolveAttached = resolve
  })
  const playerPromise = attachedPromise.then((main) => main.playerPromise)

  // The main player arrives with a dynamic import, but the page starts sending commands right away
  // (a `#quiz` deep link pauses at compile time, an overlay may open during load). Commands are kept
  // in order until it is there, and applied to whichever main player is attached at that point.
  let pendingCommands: ((main: MainPlayer) => void)[] = []

  function command(action: (main: MainPlayer) => void): void {
    if (attached) {
      action(attached)
    } else {
      pendingCommands.push(action)
    }
  }

  export function play(): void {
    command((main) => main.play())
  }

  export function pause(): void {
    command((main) => main.pause())
  }

  export function togglePlayback(): void {
    command((main) => main.togglePlayback())
  }

  export function pauseAndExitFullscreen(): void {
    command((main) => main.pauseAndExitFullscreen())
  }

  export function preventPlayback(): void {
    command((main) => main.preventPlayback())
  }

  export function allowPlayback(): void {
    command((main) => main.allowPlayback())
  }

  export function suspendForModal(): void {
    command((main) => main.suspendForModal())
  }

  export function resumeAfterModal(): void {
    command((main) => main.resumeAfterModal())
  }

  export function playbackState(): PlaybackState | undefined {
    return attached?.playbackState()
  }

  export function seek(seconds: number): void {
    command((main) => main.seek(seconds))
  }

  export function player(): Promise<PlayerInstance> {
    return playerPromise
  }

  let offScreen = $state(false)
  // A hidden player (`display: none`, e.g. behind the quiz tab) is not intersecting either, but it has not
  // been scrolled away and must not dock. Tracked separately: the IntersectionObserver stays silent when a
  // player that is already out of view gets hidden.
  let hidden = $state(false)
  let playing = $state(false)
  let intersectionObserver: IntersectionObserver | undefined

  const registration: RegisteredPlayer = {
    get container() {
      return containerElement!
    },
    get offScreen() {
      return offScreen && !hidden
    },
    get playing() {
      return playing
    },
    get playbackLocked() {
      return preAdScreenShown
    },
    player: playerPromise,
    pause,
    togglePlayback,
    refreshVisibility() {
      // A new observation has no previous result to compare against, so it always reports the current one.
      intersectionObserver?.unobserve(playerElement)
      intersectionObserver?.observe(playerElement)
    },
  }

  const docked = $derived(players.docked === registration)

  const geometry = $derived(players.slotGeometry)
  const geometryStyle = $derived(
    geometry
      ? `--pip-left: ${geometry.left}px; --pip-width: ${geometry.width}px; --pip-height: ${geometry.height}px`
      : undefined,
  )

  let started = $state(false)

  $effect(() => {
    if (!dockOwner) {
      return
    }
    return players.claimDock(registration)
  })

  function handlePlaying(): void {
    started = true
    playing = true
    players.claimPlayback(registration)
    onPlaying?.()
  }

  function handlePaused(): void {
    playing = false
    onPaused?.()
  }

  function handleAdStarted(): void {
    started = true
  }

  function handleAdPlaying(): void {
    started = true
    playing = true
    players.claimPlayback(registration)
  }

  function handleAdPaused(): void {
    playing = false
  }

  let trackedPip = false

  $effect(() => {
    const pipVisible = docked
    if (pipVisible === trackedPip) {
      return
    }
    trackedPip = pipVisible
    // tracking: player.pip_toggle
    trackEvent('player.togglePipVisible', { pipVisible }, { from: playerElement })
  })

  $effect(() => {
    onDockedChange?.(docked)
  })

  onMount(() => {
    const unregister = players.register(registration)

    ;(containerElement as HTMLElement & { playerPromise?: Promise<PlayerInstance> }).playerPromise =
      playerPromise

    intersectionObserver = new IntersectionObserver((entries) => {
      // Take the last entry because after `refreshVisibility` a stale one could arrive with the fresh one
      const entry = entries[entries.length - 1]
      offScreen = !entry.isIntersecting
      // An unrendered target reports an empty box. Read here as well, so a player that is hidden while on
      // screen does not count as off screen until the ResizeObserver below catches up.
      const { width, height } = entry.boundingClientRect
      hidden = width === 0 && height === 0
    })
    intersectionObserver.observe(playerElement)

    // Applied a frame later: reacting inside the observer's own delivery re-lays out the player and trips
    // the browser's resize-loop guard.
    let hiddenFrame = 0
    const resizeObserver = new ResizeObserver(([entry]) => {
      const { width, height } = entry.contentRect
      cancelAnimationFrame(hiddenFrame)
      hiddenFrame = requestAnimationFrame(() => {
        hidden = width === 0 && height === 0
      })
    })
    resizeObserver.observe(playerElement)

    return () => {
      intersectionObserver?.disconnect()
      intersectionObserver = undefined
      resizeObserver.disconnect()
      cancelAnimationFrame(hiddenFrame)
      unregister()
    }
  })

  const lazyMainPlayer = lazy(import.meta.env.SSR ? undefined : () => import('./main_player'))

  $effect(() => {
    const visibility = untrack(() => showAds) ? adVisibility : 'hidden'
    if (!lazyMainPlayer.ready || visibility === 'unknown') {
      return
    }
    const { attachMainPlayer } = lazyMainPlayer.imports

    untrack(() => {
      const adsActive = visibility === 'shown'
      attached = attachMainPlayer({
        container: containerElement!,
        playerElement,
        videoBanners: banners.length > 0 ? bannersElement : undefined,
        substituteLogo: substituteLogo ? substituteLogoElement : undefined,
        preAdScreen: adsActive ? preAdScreenElement : undefined,
        video: adsActive ? video : { ...video, ad_position: null },
        showAds: adsActive,
        site: site ?? undefined,
        skipVisitBookkeeping,
        startPosition,
        endPosition,
        autoplay,
        autoplayClearance,
        onPlaying: handlePlaying,
        onPaused: handlePaused,
        onFinished,
        onAdStarted: handleAdStarted,
        onAdPlaying: handleAdPlaying,
        onAdPaused: handleAdPaused,
      })
      resolveAttached(attached)
      const commands = pendingCommands
      pendingCommands = []
      for (const action of commands) {
        action(attached)
      }
    })

    return () => {
      void attached?.destroy()
      attached = undefined
      playing = false
    }
  })
</script>

<!-- The class names are load-bearing: external stylesheets style them, and `data-player--container`
     is unstyled — it is how the system specs and the native app's fullscreen compiler reach the player. -->
<div
  class="player -sixteen-to-nine"
  class:-docked={docked}
  class:-pre-ad-screen-shown={preAdScreenShown}
  style:--pre-ad-screen-height={preAdScreenShown ? `${preAdScreenHeight}px` : undefined}
  bind:this={playerElement}
  data-tracking-context={trackingContextAttribute({ player_context: docked ? 'mini' : 'main' })}
>
  <div
    class="player--container -sixteen-to-nine"
    data-player--container
    style={geometryStyle}
    bind:this={containerElement}
  >
    {#if !started}
      <img class="poster" src={video.poster_url} alt="" />
    {/if}
    {#if substituteLogo}
      <SubstituteLogo
        logo={substituteLogo}
        container={containerElement}
        bind:element={substituteLogoElement}
      />
    {/if}
    {#if showAds}
      <PreAdScreen
        container={containerElement}
        player={playerPromise}
        bind:element={preAdScreenElement}
        bind:shown={preAdScreenShown}
        bind:contentHeight={preAdScreenHeight}
      />
    {/if}
    {#if banners.length > 0}
      <VideoBanners
        {banners}
        {onQuizRequested}
        {onLearningExercisesRequested}
        container={containerElement}
        onPause={pause}
        bind:element={bannersElement}
      />
    {/if}
  </div>
</div>

<style>
  .player {
    position: relative;
    min-height: 0;
    transition: min-height 0.3s var(--ease);
  }

  /* On a short (phone) player the pre-ad screen is taller than the 16:9 box, so the box grows to the screen's
     content height while it shows, pushing the page down rather than overflowing it. */
  .player.-pre-ad-screen-shown {
    min-height: var(--pre-ad-screen-height);
  }

  .player.-docked.-pre-ad-screen-shown,
  :global(.player-toggle-content.-sticky) .player.-pre-ad-screen-shown {
    min-height: 0;
  }

  .player--container {
    border-radius: var(--border-radius);
    overflow: hidden;
    isolation: isolate;
  }

  .poster {
    position: absolute;
    inset: 0;
    width: 100%;
    height: 100%;
    object-fit: contain;
  }

  .player.-docked .player--container {
    position: fixed;
    top: auto;
    right: auto;
    left: var(--pip-left);
    bottom: var(--pip-bottom);
    width: var(--pip-width);
    height: var(--pip-height);
    z-index: 10;
    overflow: hidden;
    border-radius: var(--pip-radius, var(--border-radius));
    box-shadow: var(--pip-shadow, var(--box-shadow-floating));
    background-color: var(--pip-background, transparent);
    animation: pip-arrive 0.3s var(--ease);
  }

  @keyframes pip-arrive {
    from {
      opacity: 0;
      clip-path: inset(100% 0 0 0);
    }

    to {
      opacity: 1;
      clip-path: inset(0);
    }
  }

  @media (prefers-reduced-motion: reduce) {
    .player {
      transition: none;
    }

    .player.-docked .player--container {
      animation: none;
    }
  }

  .player.-docked .player--container :global(.video-banners) {
    display: none;
  }
</style>
